// Contrato com o backend (POST /discounts), entrada com dados preenchidos e leitura do erro do servidor.
// Fica separado do model.ts de propósito: aqui nada decide regra de negócio, só converte entre o rascunho e o
// que a API fala. Importa o model.ts; o model.ts não importa este arquivo.
import { __t } from '../../../i18n/t'
import { BR_OFFSET, addDays, clip, dayOf, hhmmOf, isoAt, toHtml, toInt, todayStr } from './format'
import {
  NAME_MAX, TOP_FUELS, activeRows, allDayWindow, autoBody, collisions, earnOn, freshDraft, isAllDayWindow, isOvernightWindow, nameValue, ptsOf, selIds,
  showsEarn, showsLimit, spillOf, windowOn,
} from './model'
import {
  Audience, Ctx, DayWindow, DefaultInfo, DiscountKind, DiscountPayload, Draft, Frequency, FuelDiscountPayload, FuelEntry, Hours, LimitMode, ListOrigin,
  NotificationPayload, Opt, SendMode, WeekScheduleItem, When,
} from './types'

// ─── Payload do POST /discounts ─────────────────────────────────────────────────

const weekItems = (d: Draft): WeekScheduleItem[] => {
  const day = d.startDate
  return d.weekdays.map((active, dayOfTheWeek) => {
    if (!active) return { dayOfTheWeek, isActive: false, startTime: null, endTime: null }
    // "O dia todo" é 00:00-23:59; no horário por dia (cópia) cada dia leva a janela que tinha.
    const w = windowOn(d, dayOfTheWeek) ?? allDayWindow()
    return { dayOfTheWeek, isActive: true, startTime: isoAt(day, w.start), endTime: isoAt(day, w.end) }
  })
}

/**
 * Corpo do POST /discounts. Segue a allowlist do Joi (discounts-validator.ts): nenhuma chave fora dela,
 * nunca "", enums explícitos, data e hora em ISO com -03:00, `weekSchedual` (sic) com 7 itens.
 */
export function toPayload(d: Draft, c: Ctx): DiscountPayload {
  const kind = d.kind as DiscountKind
  const fuelDiscounts: FuelDiscountPayload[] = activeRows(d, c).flatMap(r =>
    r.opts.map(o => ({
      fuelId: r.id,
      alterPriceValue: (o.cents as number) / 100,
      alterPriceType: 'discount' as const,
      alterPriceValueType: 'cents' as const,
      costValueType: 'points' as const,
      costValue: ptsOf(d, o),
      uniqueDiscountValue: true as const,
    })))
  const payload: DiscountPayload = { chainId: c.chainId, type: kind, stationIds: selIds(d, c), fuelDiscounts }

  if (kind === DiscountKind.special) payload.description = nameValue(d, c).trim()
  else if (kind === DiscountKind.reward || kind === DiscountKind.recovery) payload.description = nameValue(d, c).trim() || null

  if (showsLimit(kind)) {
    const limited = d.hasLimit && toInt(d.limitN) >= 1
    payload.usageLimit = limited ? toInt(d.limitN) : null
    payload.intervalInHours = limited && d.limitMode === LimitMode.every ? toInt(d.limitHours) : null
  }
  if (showsEarn(kind)) payload.pointsWhenDiscounting = earnOn(d, c)

  if (kind !== DiscountKind.special) return payload

  payload.overlapsStandardDiscount = d.overlap && collisions(d, c).length > 0
  if (d.audience === Audience.list) {
    if (d.customerIds.length) payload.customerIds = d.customerIds
  } else if (d.audience === Audience.some || d.audience === Audience.all) {
    if (d.audience === Audience.some) {
      if (d.tags.length) payload.customerTagIds = d.tags
      if (d.useGroups && d.groups.length) payload.customerGroupIds = d.groups
    }
    // A cidade restringe o público escolhido, inclusive "todos os clientes" (todos os clientes dessas cidades).
    if (d.useCity && d.cities.length) payload.cityIds = d.cities
  }

  if (d.when === When.period) {
    payload.isSchedule = true
    payload.startAt = isoAt(d.startDate, '00:00')
    // Janela do último dia que passa da meia-noite: o período vai até o fim dela, no dia seguinte.
    const spill = spillOf(d)
    payload.endAt = spill ? isoAt(spill.day, spill.time) : isoAt(d.endDate, '23:59', '59')
    if (d.hours === Hours.daily) {
      payload.frequency = Frequency.daily
      payload.startTime = isoAt(d.startDate, d.startTime)
      payload.endTime = isoAt(d.startDate, d.endTime)
    } else if (d.hours === Hours.weekdays) {
      payload.frequency = Frequency.weekly
      payload.weekSchedual = weekItems(d)
    } else {
      payload.frequency = Frequency.never
    }
  } else {
    payload.isSchedule = false
  }

  if (d.notify) {
    const notification: NotificationPayload = {
      title: d.title.trim(),
      body: toHtml(d.body.trim() || autoBody(d, c)),
      pushNotification: true,
      pushMessage: d.pushText.trim(),
      sms: false,
      sendNow: d.sendMode === SendMode.now,
      sendAt: d.sendMode === SendMode.later ? `${d.sendAt}:00${BR_OFFSET}` : null,
    }
    payload.hasNotification = true
    payload.notification = notification
  } else {
    payload.hasNotification = false
  }
  return payload
}

// ─── Entrada com dados preenchidos (clonar, relatório, Painel 360) ──────────────

export interface Prefill {
  draft: Draft
  /** Aviso azul no topo da etapa: o que foi copiado e o que não foi. */
  note: string
  /** O desconto usa Dotz, porcentagem, prefixado, acréscimo ou mais de 3 opções por combustível: só o cadastro completo cobre. */
  unsupported: boolean
}

const toNum = (v: unknown): number | null => {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** Um dia da lista `dayScheduledDiscounts` do registro -> janela HH:mm. Sem hora nenhuma, vale o dia todo. */
const windowOfRow = (row: any): DayWindow => {
  const start = hhmmOf(row?.startTime)
  const end = hhmmOf(row?.endTime)
  return !start && !end ? allDayWindow() : { start: start || '00:00', end: end || '23:59' }
}

/**
 * Texto do aviso da cópia. Cada combinação é uma frase inteira (no espanhol a ordem muda):
 * datas mantidas, apagadas (a promoção já tinha começado) ou escolhidas pelo dono; com ou sem a lista de clientes,
 * que não vem no GET e que o dono precisa escolher de novo.
 */
function copyNote(nome: string, listLost: boolean, dates: 'kept' | 'cleared' | 'chosen'): string {
  if (listLost) {
    if (dates === 'cleared') return __t('Cópia de “%{nome}”. Escolha o público em “Para quem” e as novas datas em “Quando”. A notificação não é copiada.', { nome })
    if (dates === 'chosen') return __t('Cópia de “%{nome}”, com o período que você escolheu. Escolha o público em “Para quem”. A notificação não é copiada.', { nome })
    return __t('Cópia de “%{nome}”. Escolha o público em “Para quem”. A notificação não é copiada.', { nome })
  }
  if (dates === 'cleared') return __t('Cópia de “%{nome}”. Escolha as novas datas em “Quando”. A notificação não é copiada.', { nome })
  if (dates === 'chosen') return __t('Cópia de “%{nome}”, com o período que você escolheu. A notificação não é copiada.', { nome })
  return __t('Cópia de “%{nome}”. A notificação não é copiada.', { nome })
}

/**
 * Router state -> rascunho. Dois formatos:
 *  - `{ customerIds, type: 'special', origin }` (botão "Criar Desconto" dos relatórios e Painel 360): trava em Especial + lista.
 *  - o registro de um desconto (GET /discounts/{id}), do "Duplicar" da lista.
 * Opcional no segundo: `clearDates: true` e `datesChosen: true` ("Duplicar com novas datas": as datas já são as novas).
 *
 * A lista de clientes de uma promoção (`customer_discount`) não vem na cópia. Com `hasCustomers`, o público fica sem
 * escolha (`Audience.unset`) e o dono decide em "Para quem": nunca vira "Todos os clientes" sozinho.
 */
export function fromRouterState(state: unknown, c: Pick<Ctx, 'now'>): Prefill | null {
  const s = (state && typeof state === 'object' ? state : null) as Record<string, any> | null
  if (!s) return null
  const base = freshDraft()
  const customerIds: string[] = Array.isArray(s.customerIds) ? s.customerIds.filter((x: unknown) => typeof x === 'string') : []

  if (customerIds.length && !Array.isArray(s.fuelDiscounts)) {
    const listOrigin = s.origin === ListOrigin.rfm ? ListOrigin.rfm : ListOrigin.report
    return {
      draft: { ...base, kind: DiscountKind.special, audience: Audience.list, customerIds, listOrigin, preset: { pick: TOP_FUELS, cents: null } },
      note: '', unsupported: false,
    }
  }

  const kind = Object.values(DiscountKind).find(k => k === s.type)
  if (!kind || !Array.isArray(s.fuelDiscounts)) return null

  const rows: any[] = s.fuelDiscounts
  const formats = rows.some(r =>
    r.alterPriceType === 'increase' || r.costValueType === 'dotz' || (r.alterPriceValueType && r.alterPriceValueType !== 'cents'))

  const fuels: Record<string, FuelEntry> = {}
  const seen = new Set<string>()
  let tooManyOptions = false
  rows.forEach(r => {
    const fuelId = String(r.fuelId || '')
    const cents = toNum(r.alterPriceValue) == null ? null : Math.round((toNum(r.alterPriceValue) as number) * 100)
    const pts = Math.round(toNum(r.costValue) ?? 0)
    const dedupe = [fuelId, cents, pts].join('|')
    if (!fuelId || seen.has(dedupe)) return
    seen.add(dedupe)
    // O dono confirma de novo um valor de R$ 1,00 ou mais: copiar não é confirmar.
    const opt: Opt = { cents, payPoints: kind !== DiscountKind.reward && pts > 0, pts: pts > 0 && kind !== DiscountKind.reward ? String(pts) : '', okHigh: false }
    const entry = fuels[fuelId]
    if (!entry) fuels[fuelId] = { on: true, opts: [opt] }
    else if (entry.opts.length < 3) entry.opts.push(opt)
    else tooManyOptions = true
  })
  const unsupported = formats || tooManyOptions

  const places: string[] = Array.isArray(s.placeDiscounts) ? s.placeDiscounts.map((p: any) => p.placeId) : Array.isArray(s.placeIds) ? s.placeIds : []
  const tags: string[] = Array.isArray(s.customerTagIds) ? s.customerTagIds : []
  const groups: string[] = Array.isArray(s.customerGroupIds) ? s.customerGroupIds : []
  const cities: string[] = Array.isArray(s.cityIds) ? s.cityIds : []
  const limitN = toNum(s.usageLimit)
  const limitH = toNum(s.intervalInHours)
  const rawName = String(s.description || '').trim()
  const special = kind === DiscountKind.special

  const draft: Draft = {
    ...base,
    kind, stations: Array.from(new Set(places.filter(Boolean))), fuels, preset: { pick: 'none', cents: null },
    hasLimit: !!limitN && limitN >= 1, limitN: limitN && limitN >= 1 ? String(limitN) : '1',
    limitMode: limitH && limitH >= 1 ? LimitMode.every : LimitMode.total, limitHours: limitH && limitH >= 1 ? String(limitH) : '24',
    earnPoints: typeof s.pointsWhenDiscounting === 'boolean' ? s.pointsWhenDiscounting : null,
    overlap: s.overlapsStandardDiscount === true,
    name: clip(`${rawName || ''} ${__t('(cópia)')}`.trim(), NAME_MAX), nameEdited: kind !== DiscountKind.default && !!rawName,
  }
  if (!rawName) { draft.name = ''; draft.nameEdited = false }

  let dates: 'kept' | 'cleared' | 'chosen' = 'kept'
  let listLost = false
  if (special) {
    const start = dayOf(s.startAt)
    let end = dayOf(s.endAt)
    // Sem a flag do servidor (backend antigo), promoção sem data e sem etiqueta, grupo ou cidade é lista de clientes.
    listLost = s.hasCustomers === true || (typeof s.hasCustomers !== 'boolean' && !start && !end && !tags.length && !groups.length && !cities.length)
    draft.tags = tags
    draft.groups = groups
    draft.useGroups = groups.length > 0
    draft.cities = cities
    draft.useCity = cities.length > 0
    if (listLost) draft.audience = Audience.unset
    else if (tags.length || groups.length) draft.audience = Audience.some
    // só cidade (sem etiqueta nem grupo): todos os clientes dessas cidades
    else draft.audience = Audience.all

    const sched: any[] = (Array.isArray(s.dayScheduledDiscounts) ? s.dayScheduledDiscounts : [])
      .filter((r: any) => Number.isInteger(r?.dayOfTheWeek) && r.dayOfTheWeek >= 0 && r.dayOfTheWeek <= 6)
    const wins = sched.map(windowOfRow)
    const hasWindows = (s.frequency === Frequency.daily || s.frequency === Frequency.weekly) && sched.length > 0
    const sameWindow = wins.every(w => w.start === wins[0].start && w.end === wins[0].end)
    // O período acaba no dia seguinte quando a janela do último dia passa da meia-noite (ver spillOf): volta ao último dia.
    if (end && hasWindows) {
      const clock = hhmmOf(s.endAt)
      if (clock !== '23:59' && wins.some(w => isOvernightWindow(w) && w.end === clock)) end = addDays(end, -1)
    }
    if (start && end) {
      draft.when = When.period
      const gone = s.clearDates === true || start < todayStr(c.now)
      if (gone) dates = 'cleared'
      else { draft.startDate = start; draft.endDate = end; if (s.datesChosen === true) dates = 'chosen' }
      if (hasWindows) {
        if (s.frequency === Frequency.daily && sameWindow) {
          draft.hours = Hours.daily
          draft.startTime = wins[0].start
          draft.endTime = wins[0].end
        } else {
          draft.hours = Hours.weekdays
          const weekdays = [false, false, false, false, false, false, false]
          const byDay: (DayWindow | null)[] = [null, null, null, null, null, null, null]
          sched.forEach((r, i) => { weekdays[r.dayOfTheWeek] = true; byDay[r.dayOfTheWeek] = wins[i] })
          draft.weekdays = weekdays
          if (sameWindow) {
            draft.allDay = isAllDayWindow(wins[0])
            if (!draft.allDay) { draft.startTime = wins[0].start; draft.endTime = wins[0].end }
          } else {
            // Cada dia tem o seu horário: a cópia leva os horários como estão (não aplica o do primeiro dia a todos).
            draft.allDay = false
            draft.dayWindows = byDay
          }
        }
      }
    } else {
      draft.when = When.always
    }
  }

  const label = rawName || String(s.identify || '')
  const note = unsupported ? ''
    : special ? copyNote(label, listLost, dates)
      : __t('Cópia de %{nome}. Confira os postos e os valores antes de salvar.', { nome: label })
  return { draft, note, unsupported }
}

// ─── Resposta perdida: salvou ou não? ───────────────────────────────────────────

/** Sem status (rede caiu, tempo esgotado no navegador) ou 5xx (504 do balanceador): o servidor pode ter criado o desconto. */
export function isAmbiguousFailure(error: unknown): boolean {
  const status = Number((error as { status?: unknown } | null | undefined)?.status)
  return !Number.isFinite(status) || status === 0 || status >= 500
}

/** Folga entre o relógio do navegador e o do servidor ao comparar a hora de criação. */
export const RETRY_SKEW_MS = 10 * 60 * 1000

const sameSet = (a: string[], b: string[]) => {
  const x = Array.from(new Set(a)).sort()
  const y = Array.from(new Set(b)).sort()
  return x.length === y.length && x.every((v, i) => v === y[i])
}
const instant = (v: unknown): number | null => {
  if (v == null || v === '') return null
  const t = Date.parse(String(v))
  return Number.isNaN(t) ? null : t
}
const optKey = (fuelId: unknown, value: unknown, cost: unknown) => `${fuelId}|${Math.round(Number(value) * 100)}|${Math.round(Number(cost) || 0)}`

/**
 * Depois de uma resposta perdida, procura entre os descontos recentes de quem estava salvando o que o servidor
 * pode ter criado: mesmo tipo, mesmos postos, valores, limite, nome e período, criado depois de o salvamento começar.
 * Achou: mostra a tela de salvo dele em vez de criar uma segunda cópia.
 */
export function findSavedDuplicate(payload: DiscountPayload, candidates: unknown[], sinceMs: number): any | null {
  const wantOpts = payload.fuelDiscounts.map(f => optKey(f.fuelId, f.alterPriceValue, f.costValue))
  const wantDescription = (payload.description ?? '').trim()
  const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : [])
  return (candidates as any[]).find(r => {
    if (!r || r.type !== payload.type) return false
    const created = instant(r.createdAt)
    if (created == null || created < sinceMs - RETRY_SKEW_MS) return false
    const places = list(r.placeIds).length ? list(r.placeIds) : (Array.isArray(r.placeDiscounts) ? r.placeDiscounts.map((p: any) => String(p.placeId)) : [])
    if (!sameSet(places, payload.stationIds)) return false
    const opts = (Array.isArray(r.fuelDiscounts) ? r.fuelDiscounts : []).map((f: any) => optKey(f.fuelId, f.alterPriceValue, f.costValue))
    if (!sameSet(opts, wantOpts)) return false
    if ((r.description ?? '').trim() !== wantDescription && payload.type !== DiscountKind.default) return false
    if ((r.usageLimit ?? null) !== (payload.usageLimit ?? null) || (r.intervalInHours ?? null) !== (payload.intervalInHours ?? null)) return false
    if (instant(r.startAt) !== instant(payload.startAt) || instant(r.endAt) !== instant(payload.endAt)) return false
    if (!sameSet(list(r.customerTagIds), payload.customerTagIds ?? []) || !sameSet(list(r.customerGroupIds), payload.customerGroupIds ?? [])
      || !sameSet(list(r.cityIds), payload.cityIds ?? [])) return false
    return true
  }) ?? null
}

// ─── Erro do servidor: caminho técnico -> frase para o dono do posto ────────────

/** `error.body.errors` ({ "fuelDiscounts.0.fuelId": "Campo obrigatório" }) -> linhas legíveis. */
export function describeServerErrors(errors: unknown, payload: DiscountPayload, c: Ctx): string[] {
  if (!errors || typeof errors !== 'object') return []
  const lines: string[] = []
  Object.entries(errors as Record<string, unknown>).forEach(([path, reason]) => {
    if (path === 'root.serverError' || typeof reason !== 'string' || !reason) return
    lines.push(`${serverFieldLabel(path, payload, c)}: ${__t(reason)}`)
  })
  return lines
}

const fuelName = (c: Ctx, id: string): string | undefined => c.fuels.find(f => f.id === id)?.name

function serverFieldLabel(path: string, payload: DiscountPayload, c: Ctx): string {
  const fuel = /^fuelDiscounts\.(\d+)\.(\w+)$/.exec(path)
  if (fuel) {
    const row = payload.fuelDiscounts[Number(fuel[1])]
    const name = row ? fuelName(c, row.fuelId) : undefined
    const what = fuel[2] === 'alterPriceValue' ? __t('desconto por litro') : fuel[2] === 'costValue' ? __t('pontos') : __t('combustível')
    return name ? `${name} (${what})` : what
  }
  if (path === 'description') return __t('Nome do desconto')
  if (path.startsWith('stationIds')) return __t('Postos')
  if (path === 'startAt' || path === 'endAt') return __t('Período')
  if (path === 'startTime' || path === 'endTime' || path.startsWith('weekSchedual')) return __t('Dias e horários')
  if (path === 'usageLimit' || path === 'intervalInHours') return __t('Limite de uso')
  if (/^(customerTagIds|customerGroupIds|cityIds|customerIds)/.test(path)) return __t('Público')
  if (path === 'notification.title') return __t('Título da notificação')
  if (path === 'notification.pushMessage') return __t('Texto da notificação')
  if (path === 'notification.body') return __t('Mensagem ao tocar')
  if (path.startsWith('notification')) return __t('Notificação')
  return path
}

/**
 * Depois de salvar um Padrão que dividiu outro: qual código o Padrão que sobrou nos outros postos ganhou.
 * É o Padrão em vigor que cobre esses postos e não existia antes (nem é o que acabamos de criar).
 */
export function findSplitCodes(before: DefaultInfo[], selected: string[], after: DefaultInfo[], newCode: string): Record<string, string> {
  const known = new Set([...before.map(x => x.code), newCode])
  const out: Record<string, string> = {}
  before.forEach(def => {
    if (!def.stationIds.some(id => selected.includes(id))) return
    const rest = def.stationIds.filter(id => !selected.includes(id))
    if (!rest.length) return
    const copy = after.find(x => !known.has(x.code) && rest.every(id => x.stationIds.includes(id)))
    if (copy) out[def.code] = copy.code
  })
  return out
}

